import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaTelegram, FaGift, FaArrowLeft, FaCheckCircle, FaUser, FaClipboard, FaTimes, FaUserShield } from 'react-icons/fa';
import { Check, AlertCircle, Gift, Calendar, ChevronRight, Star, Award, Shield, Clock, Lock, Unlock } from 'lucide-react';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';
import WhatsAppFloat from './auth/WhatsAppFloat';
import HomeToastNotification from './auth/loaders/HomeToastNotification';

const Toast = ({ message, isVisible, onHide }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onHide();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onHide]);

  if (!isVisible) return null;

  return (
    <div className="toast-notification">
      <div className="toast-icon-wrapper">
        <Check size={16} />
      </div>
      <span className="toast-message">{message}</span>
    </div>
  );
};

const Rewards = ({ setCurrentView }) => {
  const { userDetails, token, updateUserDetails } = useAuth();
  const [referralStats, setReferralStats] = useState({ totalReferrals: 0, totalRevenue: 0 });
  const [inviteLink, setInviteLink] = useState('');
  const [showToast, setShowToast] = useState(false);

  const [rewardCode, setRewardCode] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [lastClaimDate, setLastClaimDate] = useState(null);
  const [toastNotification, setToastNotification] = useState(null);
  
  // Animation states
  const [isCodeFocused, setIsCodeFocused] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Track streak (would normally come from an API)
  const [currentStreak, setCurrentStreak] = useState(3);
  const [nextRewardAmount, setNextRewardAmount] = useState(100);

  const handleClaimReward = async () => {
    try {
      setClaimLoading(true);
      setClaimError('');
      
      const response = await axios.post(
        'https://api.visiontech2030.com/api/rewards/claim',
        { rewardCode },
        {
          headers: { 'Authorization': token }
        }
      );

      if (response.data.success) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        setToastNotification({ message: 'Reward claimed successfully!', type: 'success' });
        setRewardCode('');
        refreshUserData();
        setLastClaimDate(new Date().toISOString());
        setCurrentStreak(prev => prev + 1);
      }
    } catch (error) {
      setClaimError(error.response?.data?.error || 'Failed to claim reward');
    } finally {
      setClaimLoading(false);
    }
  };

  // Add refreshUserData function
  const refreshUserData = async () => {
    try {
      const userResponse = await axios.get(`https://api.visiontech2030.com/api/v1/auth/${userDetails.email}`, {
        headers: {
          'Authorization': token
        }
      });
      updateUserDetails(userResponse.data);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Placeholder rewards data - in real app, this would come from backend
  const futureBenefits = [
    { day: 7, reward: 'Premium Bonus', icon: <Star size={16} /> },
    { day: 14, reward: '2x Rewards', icon: <Award size={16} /> },
    { day: 30, reward: 'Elite Status', icon: <Shield size={16} /> }
  ];

  return (
    <div className="vt-rewards-container">
      {/* Header */}
      <div className="vt-rewards-header">
        <button className="vt-rewards-back-btn" onClick={() => setCurrentView('main')}>
          <FaArrowLeft />
          <span>Back</span>
        </button>
        <h2 className="vt-rewards-title">Daily Tokens</h2>
      </div>
      
      {/* Streak Section */}
      <div className="vt-streak-section">
        <div className="vt-streak-header">
          <div className="vt-streak-info">
            <div className="vt-streak-badge">
              <Clock size={16} />
              <span>Day {currentStreak}</span>
            </div>
            <h3 className="vt-streak-title">Your Reward Streak</h3>
          </div>
        </div>
        
        <div className="vt-streak-progress">
          <div className="vt-streak-bar">
            <div 
              className="vt-streak-fill" 
              style={{ width: `${Math.min(100, (currentStreak / 30) * 100)}%` }}
            ></div>
            
            {futureBenefits.map((benefit, index) => (
              <div 
                key={index}
                className={`vt-streak-milestone ${currentStreak >= benefit.day ? 'active' : ''}`}
                style={{ left: `${(benefit.day / 30) * 100}%` }}
              >
                <div className="vt-milestone-icon">
                  {benefit.icon}
                </div>
                <div className="vt-milestone-tooltip">
                  <span className="vt-milestone-day">Day {benefit.day}</span>
                  <span className="vt-milestone-reward">{benefit.reward}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="vt-streak-status">
          <div className="vt-streak-counter">
            <div className="vt-counter-value">{currentStreak}</div>
            <div className="vt-counter-label">Current<br />Streak</div>
          </div>
          <div className="vt-streak-next">
            <span>Next Reward: <strong>KES {nextRewardAmount}</strong></span>
            {lastClaimDate && (
              <div className="vt-last-claim">
                <Calendar size={14} />
                <span>Last claimed: {new Date(lastClaimDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Daily Rewards Card */}
      <div className="vt-daily-reward-card">
        {showConfetti && (
          <div className="vt-confetti-container">
            {[...Array(50)].map((_, i) => (
              <div key={i} className={`vt-confetti vt-confetti-${i % 5}`}></div>
            ))}
          </div>
        )}
        
        <div className="vt-reward-card-shine"></div>
        
        <div className="vt-reward-card-header">
          <div className="vt-reward-icon-wrapper">
            <Gift size={24} strokeWidth={2} />
          </div>
          <h3 className="vt-reward-title">Daily Reward Token</h3>
        </div>
        
        <p className="vt-reward-description">
          Enter today's reward code to claim your daily bonus and keep your streak going!
        </p>
        
        <div className="vt-reward-code-container">
          <div className={`vt-reward-input-wrapper ${isCodeFocused ? 'focused' : ''}`}>
            <div className="vt-reward-input-icon">
              {rewardCode ? <Unlock size={18} /> : <Lock size={18} />}
            </div>
            <input
              type="text"
              placeholder="Enter 6-digit reward code"
              value={rewardCode}
              onChange={(e) => setRewardCode(e.target.value.toUpperCase())}
              onFocus={() => setIsCodeFocused(true)}
              onBlur={() => setIsCodeFocused(false)}
              maxLength={6}
              className="vt-reward-input"
            />
          </div>
          
          <div className="vt-code-hint">
            <AlertCircle size={14} />
            <span>Codes are case-sensitive and valid for 24 hours</span>
          </div>
        </div>
        
        <button
          className="vt-claim-button"
          onClick={handleClaimReward}
          disabled={claimLoading || !rewardCode || rewardCode.length < 6}
        >
          {claimLoading ? (
            <>
              <div className="vt-loading-spinner"></div>
              <span>Claiming Reward...</span>
            </>
          ) : (
            <>
              <FaGift />
              <span>Claim Reward</span>
            </>
          )}
          <div className="vt-button-glow"></div>
        </button>
        
        {claimError && (
          <div className="vt-claim-error">
            <AlertCircle size={16} />
            <span>{claimError}</span>
          </div>
        )}
      </div>
      
      {/* Reward Benefits Card */}
      <div className="vt-benefits-card">
        <h3 className="vt-benefits-title">Why Claim Daily Rewards?</h3>
        
        <div className="vt-benefits-list">
          <div className="vt-benefit-item">
            <div className="vt-benefit-icon green">
              <Gift size={16} />
            </div>
            <div className="vt-benefit-content">
              <h4 className="vt-benefit-name">Daily Bonuses</h4>
              <p className="vt-benefit-desc">Earn free tokens every day to boost your investments</p>
            </div>
          </div>
          
          <div className="vt-benefit-item">
            <div className="vt-benefit-icon purple">
              <Star size={16} />
            </div>
            <div className="vt-benefit-content">
              <h4 className="vt-benefit-name">Loyalty Streak</h4>
              <p className="vt-benefit-desc">Maintain your streak for progressively larger rewards</p>
            </div>
          </div>
          
          <div className="vt-benefit-item">
            <div className="vt-benefit-icon blue">
              <Award size={16} />
            </div>
            <div className="vt-benefit-content">
              <h4 className="vt-benefit-name">Exclusive Benefits</h4>
              <p className="vt-benefit-desc">Unlock special privileges at day 7, 14, and 30</p>
            </div>
          </div>
        </div>
      </div>
      
      <BottomNavigation />
      {toastNotification && (
        <HomeToastNotification
          message={toastNotification.message}
          type={toastNotification.type}
          onClose={() => setToastNotification(null)}
        />
      )}
      <WhatsAppFloat />
      
      <style jsx="true">{`
        .vt-rewards-container {
          padding: 16px;
          max-width: 800px;
          margin-bottom: 3rem;
          color: #e2e8f0;
          min-height: 100vh;
          padding-bottom: 80px;
        }
        
        /* Header Styles */
        .vt-rewards-header {
          display: flex;
          align-items: center;
          margin-bottom: 24px;
        }
        
        .vt-rewards-back-btn {
          background: rgba(79, 70, 229, 0.1);
          color: #a5b4fc;
          border: 1px solid rgba(79, 70, 229, 0.2);
          border-radius: 8px;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-rewards-back-btn:hover {
          background: rgba(79, 70, 229, 0.2);
          transform: translateX(-2px);
        }
        
        .vt-rewards-title {
          margin: 0 0 0 16px;
          font-size: 24px;
          font-weight: 700;
          color: #f8fafc;
          background: linear-gradient(to right, #fff, #a5b4fc);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        /* Streak Section Styles */
        .vt-streak-section {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .vt-streak-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .vt-streak-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .vt-streak-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(135deg, rgba(167, 139, 250, 0.1), rgba(110, 231, 183, 0.1));
          border-radius: 20px;
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 600;
          color: #a78bfa;
          border: 1px solid rgba(167, 139, 250, 0.2);
        }
        
        .vt-streak-title {
          font-size: 18px;
          font-weight: 600;
          color: #f8fafc;
          margin: 0;
        }
        
        .vt-streak-progress {
          margin-bottom: 20px;
          position: relative;
        }
        
        .vt-streak-bar {
          height: 8px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          overflow: hidden;
          position: relative;
          margin: 16px 0 24px;
        }
        
        .vt-streak-fill {
          height: 100%;
          background: linear-gradient(to right, #4f46e5, #a78bfa);
          border-radius: 4px;
          transition: width 0.5s ease;
        }
        
        .vt-streak-milestone {
          position: absolute;
          top: 0;
          transform: translateX(-50%);
          z-index: 2;
        }
        
        .vt-milestone-icon {
          width: 24px;
          height: 24px;
          background: #1e293b;
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.5);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }
        
        .vt-streak-milestone.active .vt-milestone-icon {
          background: #4f46e5;
          border-color: #a78bfa;
          color: white;
          box-shadow: 0 0 12px rgba(79, 70, 229, 0.5);
        }
        
        .vt-milestone-tooltip {
          position: absolute;
          bottom: 36px;
          left: 50%;
          transform: translateX(-50%) scale(0.9);
          background: #1e293b;
          border: 1px solid rgba(79, 70, 229, 0.3);
          border-radius: 8px;
          padding: 8px 12px;
          min-width: 100px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          pointer-events: none;
          text-align: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .vt-milestone-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-width: 6px;
          border-style: solid;
          border-color: #1e293b transparent transparent transparent;
        }
        
        .vt-milestone-icon:hover + .vt-milestone-tooltip {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) scale(1);
        }
        
        .vt-milestone-day {
          font-size: 11px;
          font-weight: 600;
          color: #a78bfa;
        }
        
        .vt-milestone-reward {
          font-size: 12px;
          color: #e2e8f0;
        }
        
        .vt-streak-status {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        
        .vt-streak-counter {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(124, 58, 237, 0.1));
          border: 1px solid rgba(79, 70, 229, 0.2);
          color: #a78bfa;
          text-align: center;
        }
        
        .vt-counter-value {
          font-size: 20px;
          font-weight: 700;
          color: #e2e8f0;
          line-height: 1;
        }
        
        .vt-counter-label {
          font-size: 9px;
          font-weight: 500;
          color: #94a3b8;
          line-height: 1.2;
          margin-top: 2px;
        }
        
        .vt-streak-next {
          flex: 1;
          font-size: 14px;
          color: #cbd5e1;
        }
        
        .vt-streak-next strong {
          color: #a78bfa;
          font-weight: 600;
        }
        
        .vt-last-claim {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94a3b8;
          margin-top: 4px;
        }
        
        /* Daily Reward Card Styles */
        .vt-daily-reward-card {
          background: linear-gradient(135deg, #1a1c3f 0%, #1e1b4b 100%);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          border: 1px solid rgba(79, 70, 229, 0.2);
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          z-index: 1;
        }
        
        .vt-reward-card-shine {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(45deg, 
            rgba(255, 255, 255, 0) 0%, 
            rgba(255, 255, 255, 0.03) 40%, 
            rgba(255, 255, 255, 0.05) 50%, 
            rgba(255, 255, 255, 0.03) 60%, 
            rgba(255, 255, 255, 0) 100%);
          background-size: 200% 200%;
          animation: shine 3s infinite linear;
          z-index: -1;
        }
        
        @keyframes shine {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        .vt-reward-card-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }
        
        .vt-reward-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(245, 158, 11, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.2);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
        }
        
        .vt-reward-title {
          font-size: 20px;
          font-weight: 700;
          color: #f8fafc;
          margin: 0;
          background: linear-gradient(to right, #f59e0b, #fbbf24);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .vt-reward-description {
          font-size: 15px;
          color: #cbd5e1;
          margin-bottom: 24px;
          line-height: 1.5;
        }
        
        .vt-reward-code-container {
          margin-bottom: 24px;
        }
        
        .vt-reward-input-wrapper {
          display: flex;
          align-items: center;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 4px 16px;
          transition: all 0.3s ease;
          margin-bottom: 8px;
        }
        
        .vt-reward-input-wrapper.focused {
          border-color: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }
        
        .vt-reward-input-icon {
          color: #94a3b8;
          transition: all 0.3s ease;
        }
        
        .vt-reward-input-wrapper.focused .vt-reward-input-icon {
          color: #f59e0b;
          transform: scale(1.1);
        }
        
        .vt-reward-input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 18px;
          font-weight: 600;
          color: #f8fafc;
          padding: 12px 12px;
          letter-spacing: 2px;
          outline: none;
          caret-color: #f59e0b;
          width: 100%;
          text-align: center;
        }
        
        .vt-reward-input::placeholder {
          font-size: 16px;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.3);
          letter-spacing: 0;
        }
        
        .vt-code-hint {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94a3b8;
          margin-top: 8px;
        }
        
        .vt-claim-button {
          width: 100%;
          background: linear-gradient(45deg, #f59e0b, #ea580c);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 16px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
        }
        
        .vt-claim-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(245, 158, 11, 0.4);
        }
        
        .vt-claim-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .vt-button-glow {
          position: absolute;
          width: 40px;
          height: 100%;
          top: 0;
          left: -100px;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.3) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          animation: buttonGlow 3s infinite;
          animation-timing-function: ease-in-out;
          transform: skewX(-20deg);
        }
        
        @keyframes buttonGlow {
          0% { left: -100px; }
          20% { left: -100px; }
          60% { left: calc(100% + 100px); }
          100% { left: calc(100% + 100px); }
        }
        
        .vt-loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .vt-claim-error {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 16px;
          padding: 12px 16px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 8px;
          color: #f87171;
          font-size: 14px;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Benefits Card Styles */
        .vt-benefits-card {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .vt-benefits-title {
          font-size: 18px;
          font-weight: 600;
          color: #f8fafc;
          margin: 0 0 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-benefits-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .vt-benefit-item {
          display: flex;
          gap: 16px;
          padding: 12px;
          background: rgba(15, 23, 42, 0.3);
          border-radius: 12px;
          transition: transform 0.2s ease;
        }
        
        .vt-benefit-item:hover {
          transform: translateX(5px);
        }
        
        .vt-benefit-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .vt-benefit-icon.green {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }
        
        .vt-benefit-icon.purple {
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
          border: 1px solid rgba(167, 139, 250, 0.2);
        }
        
        .vt-benefit-icon.blue {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }
        
        .vt-benefit-content {
          flex: 1;
        }
        
        .vt-benefit-name {
          font-size: 15px;
          font-weight: 600;
          color: #f8fafc;
          margin: 0 0 4px;
        }
        
        .vt-benefit-desc {
          font-size: 13px;
          color: #94a3b8;
          margin: 0;
          line-height: 1.4;
        }
        
        /* Confetti Animation */
        .vt-confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
          z-index: 2;
        }
        
        .vt-confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          background-color: #4f46e5;
          opacity: 0.8;
          animation: confettiFall 5s ease-in-out forwards;
        }
        
        .vt-confetti-0 {
          background-color: #4f46e5;
          left: 10%;
          animation-delay: 0.1s;
          animation-duration: 3s;
        }
        
        .vt-confetti-1 {
          background-color: #f59e0b;
          left: 20%;
          animation-delay: 0.3s;
          animation-duration: 2.5s;
        }
        
        .vt-confetti-2 {
          background-color: #a78bfa;
          left: 30%;
          animation-delay: 0.5s;
          animation-duration: 3.5s;
        }
        
        .vt-confetti-3 {
          background-color: #10b981;
          left: 40%;
          animation-delay: 0.2s;
          animation-duration: 4s;
        }
        
        .vt-confetti-4 {
          background-color: #f87171;
          left: 50%;
          animation-delay: 0.4s;
          animation-duration: 3.2s;
        }
        
        @keyframes confettiFall {
          0% {
            top: -10%;
            transform: translateX(0) rotateZ(0);
            opacity: 1;
          }
          75% {
            opacity: 1;
          }
          100% {
            top: 100%;
            transform: translateX(20px) rotateZ(90deg);
            opacity: 0;
          }
        }
        
        /* Toast styles */
        .toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: rgba(16, 185, 129, 0.9);
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          animation: toastSlideIn 0.3s ease, toastFadeOut 0.3s ease 3.7s forwards;
        }
        
        @keyframes toastSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes toastFadeOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        
        .toast-icon-wrapper {
          width: 20px;
          height: 20px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .toast-message {
          font-size: 14px;
          font-weight: 500;
        }
        
        /* Responsive styles */
        @media (max-width: 768px) {
          .vt-rewards-container {
            padding: 12px;
          }
          
          .vt-rewards-title {
            font-size: 22px;
          }
          
          .vt-streak-section,
          .vt-daily-reward-card,
          .vt-benefits-card {
            padding: 20px;
          }
          
          .vt-streak-status {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          
          .vt-streak-counter {
            align-self: center;
          }
          
          .vt-benefit-item {
            padding: 10px;
          }
        }
        
        @media (max-width: 480px) {
          .vt-rewards-back-btn span {
            display: none;
          }
          
          .vt-rewards-title {
            font-size: 20px;
          }
          
          .vt-streak-title {
            font-size: 16px;
          }
          
          .vt-milestone-icon {
            width: 20px;
            height: 20px;
          }
          
          .vt-milestone-icon svg {
            width: 12px;
            height: 12px;
          }
          
          .vt-milestone-tooltip {
            min-width: 80px;
            padding: 6px 8px;
          }
          
          .vt-milestone-day {
            font-size: 9px;
          }
          
          .vt-milestone-reward {
            font-size: 10px;
          }
          
          .vt-reward-icon-wrapper {
            width: 40px;
            height: 40px;
          }
          
          .vt-reward-icon-wrapper svg {
            width: 20px;
            height: 20px;
          }
          
          .vt-reward-title {
            font-size: 18px;
          }
          
          .vt-reward-description {
            font-size: 14px;
          }
          
          .vt-reward-input {
            font-size: 16px;
            padding: 10px 8px;
          }
          
          .vt-reward-input::placeholder {
            font-size: 14px;
          }
          
          .vt-claim-button {
            padding: 14px;
            font-size: 15px;
          }
          
          .vt-benefits-title {
            font-size: 16px;
          }
          
          .vt-benefit-name {
            font-size: 14px;
          }
          
          .vt-benefit-desc {
            font-size: 12px;
          }
          
          .vt-benefit-icon {
            width: 28px;
            height: 28px;
          }
          
          .vt-benefit-icon svg {
            width: 14px;
            height: 14px;
          }
          
          .vt-confetti {
            width: 8px;
            height: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default Rewards;